import { useEffect, useState, useMemo } from 'react'
import { profileApi } from '../../services/api'
import { getRoleLabel } from '../../hooks/useAuth'

interface Profile {
  id: number
  email: string
  full_name: string
  role: string
  status: string
  dni: string | null
  phone: string | null
  residency: string | null
  created_at: string
  panels?: string[]
}

const PERU_DEPARTMENTS: Record<string, Record<string, string[]>> = {
  'Lima': {
    'Lima': ['Cercado de Lima', 'Ancón', 'Ate', 'Barranco', 'Breña', 'Carabayllo', 'Chaclacayo', 'Chorrillos', 'Cieneguilla', 'Comas', 'El Agustino', 'Independencia', 'Jesús María', 'Jesús Nazareno', 'La Molina', 'La Victoria', 'Lince', 'Los Olivos', 'Lurigancho', 'Lurín', 'Magdalena del Mar', 'Magdalena Vieja', 'Maringá', 'Matta Sur', 'Pachacámac', 'Pachacutec', 'Punta Hermosa', 'Punta Negra', 'Rímac', 'San Bartolo', 'San Borja', 'San Isidro', 'San Martín de Porres', 'San Martín de Porres', 'San Juan de Miraflores', 'San Juan de Lurigancho', 'San Luis', 'San Miguel', 'Santa Anita', 'Santa María del Mar', 'Santa Rosa', 'Santiago de Surco', 'Surquillo', 'Villa El Salvador', 'Villa María del Triunfo'],
  },
  'Arequipa': {
    'Arequipa': ['Arequipa', 'Alto Selva Alegre', 'Cayma', 'Cerro Colorado', 'Characato', 'Chiguata', 'Jacobo Hunter', 'La Joya', 'Mariano Melgar', 'Miraflores', 'Mollebaya', 'Paucarpata', 'Pocsi', 'Polobaya', 'Quequeña', 'Sabandía', 'Sachaca', 'San Juan de Siguas', 'San Juan de Tarucani', 'Santa Isabel de Siguas', 'Santa Rita de Siguas', 'Socabaya', 'Tiabaya', 'Uchumayo', 'Vítor', 'Yanahuara', 'Yarabamba', 'Yura'],
  },
  'Cusco': {
    'Cusco': ['Cusco', 'Acomayo', 'Calca', 'Chincheros', 'Coropuna', 'Espinar', 'La Convención', 'Paruro', 'Paucartambo', 'Quispicanchi', 'Urubamba'],
  },
  'Junín': {
    'Huancayo': ['Huancayo', 'Chupaca', 'Concepción', 'Jauja', 'Junín', 'Satipo', 'Tarma'],
  },
  'Piura': {
    'Piura': ['Piura', 'Ayabaca', 'Huancabamba', 'Morropón', 'Paita', 'Sechura', 'Sullana', 'Talara'],
  },
  'La Libertad': {
    'Trujillo': ['Trujillo', 'Ascope', 'Chepen', 'Julcán', 'Otuzco', 'Pacasmayo', 'Pataz', 'Sánchez Carrión', 'Santiago de Chuco', 'Virú'],
  },
  'Lambayeque': {
    'Chiclayo': ['Chiclayo', 'Ferreñafe', 'Lambayeque'],
  },
  'Callao': {
    'Callao': ['Callao', 'Bellavista', 'Carmen de la Legua', 'La Perla', 'La Punta', 'Ventanilla'],
  },
}

export default function ProfilePage() {
  const [profile, setProfile] = useState<Profile | null>(null)
  const [loading, setLoading] = useState(true)

  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [emailPwd, setEmailPwd] = useState('')
  const [dni, setDni] = useState('')
  const [phone, setPhone] = useState('')
  const [department, setDepartment] = useState('')
  const [province, setProvince] = useState('')
  const [district, setDistrict] = useState('')
  const [profileSaving, setProfileSaving] = useState(false)
  const [profileMsg, setProfileMsg] = useState({ type: '', text: '' })

  const [currentPwd, setCurrentPwd] = useState('')
  const [newPwd, setNewPwd] = useState('')
  const [confirmPwd, setConfirmPwd] = useState('')
  const [pwdSaving, setPwdSaving] = useState(false)
  const [pwdMsg, setPwdMsg] = useState({ type: '', text: '' })

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await profileApi.get()
        const p = res.data
        setProfile(p)
        setFullName(p.full_name || '')
        setEmail(p.email || '')
        setDni(p.dni || '')
        setPhone(p.phone || '')
        if (p.residency) {
          const parts = p.residency.split(',').map((s: string) => s.trim())
          if (parts.length >= 2) setDepartment(parts[1] || '')
          if (parts.length >= 3) setProvince(parts[2] || '')
          if (parts.length >= 4) setDistrict(parts[3] || '')
        }
      } catch (err) {
        console.error('Error fetching profile:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchProfile()
  }, [])

  const provinces = useMemo(() => {
    if (!department || !PERU_DEPARTMENTS[department]) return []
    return Object.keys(PERU_DEPARTMENTS[department])
  }, [department])

  const districts = useMemo(() => {
    if (!department || !province) return []
    return PERU_DEPARTMENTS[department]?.[province] || []
  }, [department, province])

  const handleDepartmentChange = (value: string) => {
    setDepartment(value)
    setProvince('')
    setDistrict('')
  }

  const handleProvinceChange = (value: string) => {
    setProvince(value)
    setDistrict('')
  }

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!fullName.trim()) {
      setProfileMsg({ type: 'error', text: 'El nombre completo es requerido' })
      return
    }
    const emailChanged = email.trim().toLowerCase() !== (profile?.email || '').toLowerCase()
    if (emailChanged && !emailPwd) {
      setProfileMsg({ type: 'error', text: 'Ingresa tu contraseña actual para cambiar el correo' })
      return
    }
    setProfileSaving(true)
    setProfileMsg({ type: '', text: '' })
    try {
      const residencyParts = ['Perú', department, province, district].filter(Boolean)
      await profileApi.update({
        full_name: fullName,
        email: email.trim().toLowerCase(),
        current_password: emailChanged ? emailPwd : undefined,
        dni: dni || null,
        phone: phone || null,
        residency: residencyParts.length > 1 ? residencyParts.join(', ') : null,
      })
      setProfile((prev) => (prev ? { ...prev, email: email.trim().toLowerCase() } : prev))
      setEmailPwd('')
      setProfileMsg({ type: 'success', text: 'Perfil actualizado correctamente' })
    } catch (err: any) {
      setProfileMsg({ type: 'error', text: err.response?.data?.error || 'Error al actualizar perfil' })
    } finally {
      setProfileSaving(false)
    }
  }

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!currentPwd) {
      setPwdMsg({ type: 'error', text: 'La contraseña actual es requerida' })
      return
    }
    if (!newPwd || newPwd.length < 6) {
      setPwdMsg({ type: 'error', text: 'La nueva contraseña debe tener al menos 6 caracteres' })
      return
    }
    if (newPwd !== confirmPwd) {
      setPwdMsg({ type: 'error', text: 'Las contraseñas no coinciden' })
      return
    }
    setPwdSaving(true)
    setPwdMsg({ type: '', text: '' })
    try {
      await profileApi.changePassword({ current_password: currentPwd, new_password: newPwd })
      setPwdMsg({ type: 'success', text: 'Contraseña cambiada correctamente' })
      setCurrentPwd('')
      setNewPwd('')
      setConfirmPwd('')
    } catch (err: any) {
      setPwdMsg({ type: 'error', text: err.response?.data?.error || 'Error al cambiar contraseña' })
    } finally {
      setPwdSaving(false)
    }
  }

  if (loading) {
    return <div className="text-center py-8">Cargando...</div>
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-espresso-800 mb-6">Mi Perfil</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Personal Information */}
        <div className="bg-cream-50 shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-espresso-800 mb-4">Información Personal</h2>
          <div className="bg-cream-50 rounded-lg p-4 mb-4 text-sm text-espresso-600">
            <div><span className="font-medium text-espresso-700">Rol:</span> {profile ? getRoleLabel(profile.role, profile.panels) : '-'}</div>
            <div><span className="font-medium text-espresso-700">Miembro desde:</span> {profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : '-'}</div>
          </div>
          <form onSubmit={handleProfileSubmit} className="space-y-4">
            {profileMsg.type && (
              <div className={`px-4 py-2 rounded text-sm ${profileMsg.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                {profileMsg.text}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-espresso-700 mb-1">Nombre Completo *</label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-espresso-700 mb-1">Correo Electrónico *</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                required
              />
            </div>
            {email.trim().toLowerCase() !== (profile?.email || '').toLowerCase() && (
              <div>
                <label className="block text-sm font-medium text-espresso-700 mb-1">Contraseña Actual *</label>
                <input
                  type="password"
                  value={emailPwd}
                  onChange={(e) => setEmailPwd(e.target.value)}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  required
                />
                <p className="text-xs text-espresso-500 mt-1">Requerida para confirmar el cambio de correo</p>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-espresso-700 mb-1">DNI</label>
                <input
                  type="text"
                  value={dni}
                  onChange={(e) => setDni(e.target.value)}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-espresso-700 mb-1">Teléfono</label>
                <input
                  type="text"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                />
              </div>
            </div>

            {/* Residency - Structured */}
            <div>
              <label className="block text-sm font-medium text-espresso-700 mb-1">Domicilio</label>
              <div className="bg-cream-50 rounded-md px-3 py-2 text-xs text-espresso-500 mb-2">Perú</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-espresso-500 mb-1">Departamento</label>
                  <select
                    value={department}
                    onChange={(e) => handleDepartmentChange(e.target.value)}
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  >
                    <option value="">Seleccionar...</option>
                    {Object.keys(PERU_DEPARTMENTS).map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-espresso-500 mb-1">Provincia</label>
                  <select
                    value={province}
                    onChange={(e) => handleProvinceChange(e.target.value)}
                    disabled={!department}
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500 disabled:bg-cream-200"
                  >
                    <option value="">Seleccionar...</option>
                    {provinces.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-espresso-500 mb-1">Distrito</label>
                  <select
                    value={district}
                    onChange={(e) => setDistrict(e.target.value)}
                    disabled={!province}
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500 disabled:bg-cream-200"
                  >
                    <option value="">Seleccionar...</option>
                    {districts.map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={profileSaving}
              className="px-4 py-2 bg-terracotta-600 text-white rounded-md text-sm font-medium hover:bg-terracotta-700 disabled:opacity-50"
            >
              {profileSaving ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </form>
        </div>

        {/* Change Password */}
        <div className="bg-cream-50 shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-espresso-800 mb-4">Cambiar Contraseña</h2>
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            {pwdMsg.type && (
              <div className={`px-4 py-2 rounded text-sm ${pwdMsg.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                {pwdMsg.text}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-espresso-700 mb-1">Contraseña Actual *</label>
              <input
                type="password"
                value={currentPwd}
                onChange={(e) => setCurrentPwd(e.target.value)}
                className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-espresso-700 mb-1">Nueva Contraseña *</label>
              <input
                type="password"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                required
              />
              <p className="text-xs text-espresso-500 mt-1">Mínimo 6 caracteres</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-espresso-700 mb-1">Confirmar Nueva Contraseña *</label>
              <input
                type="password"
                value={confirmPwd}
                onChange={(e) => setConfirmPwd(e.target.value)}
                className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                required
              />
            </div>
            <button
              type="submit"
              disabled={pwdSaving}
              className="px-4 py-2 bg-terracotta-600 text-white rounded-md text-sm font-medium hover:bg-terracotta-700 disabled:opacity-50"
            >
              {pwdSaving ? 'Cambiando...' : 'Cambiar Contraseña'}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
